import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

interface CountdownOverlayProps {
  /** Audio context used for scheduling tick sounds. */
  ctx: AudioContext;
  /** Absolute AudioContext time when the song should begin. */
  startAt: number;
  /** Seconds per beat for the pre-roll countdown. */
  beatDuration: number;
  /** Called when the countdown has finished. */
  onComplete: () => void;
  /** Whether the overlay should cover the entire screen. */
  fullScreen?: boolean;
}

/**
 * Fullscreen overlay that displays a 3-2-1 countdown and plays metronome
 * ticks aligned to the provided AudioContext timeline.
 */
export default function CountdownOverlay({
  ctx,
  startAt,
  beatDuration,
  onComplete,
  fullScreen = true,
}: CountdownOverlayProps) {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    const counts = [3, 2, 1];
    const timeouts: number[] = [];

    counts.forEach((c, i) => {
      const time = startAt - (counts.length - i) * beatDuration;
      // Schedule display update
      const delay = (time - ctx.currentTime) * 1000;
      if (delay <= 0) {
        setCount(c);
      } else {
        timeouts.push(window.setTimeout(() => setCount(c), delay));
      }
      // Schedule tick sound
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 1000;
      gain.gain.value = 0.2;
      osc.connect(gain).connect(ctx.destination);
      osc.start(time);
      osc.stop(time + 0.05);
    });

    const endDelay = Math.max(0, (startAt - ctx.currentTime) * 1000);
    const endTimeout = window.setTimeout(onComplete, endDelay);

    return () => {
      timeouts.forEach((t) => clearTimeout(t));
      clearTimeout(endTimeout);
    };
  }, [ctx, startAt, beatDuration, onComplete]);

  if (count === null) {
    return (
      <div
        className={`pointer-events-none ${
          fullScreen ? 'fixed' : 'absolute'
        } inset-0 flex items-center justify-center bg-black/50`}
      />
    );
  }

  return (
    <div
      className={`pointer-events-none ${
        fullScreen ? 'fixed' : 'absolute'
      } inset-0 flex items-center justify-center bg-black/50 text-white`}
    >
      <AnimatePresence mode="wait">
        <motion.span
          key={count}
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 1.5, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="text-8xl font-bold"
        >
          {count}
        </motion.span>
      </AnimatePresence>
    </div>
  );
}

